/* eslint-disable no-prototype-builtins */
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faQuestionCircle } from "@fortawesome/free-solid-svg-icons";
import ReactHtmlParser from "react-html-parser";
import PropTypes from "prop-types";
import { FormattedMessage } from "react-intl";
import usePermissions from "hooks/usePermissions";
import { Row, Col, Tooltip, OverlayTrigger } from "react-bootstrap";
import getLabel, { getLabelWithTranslationFallback } from "helpers/getLabel";
import { useStateMachine } from "little-state-machine";
import { replaceLinks } from "helpers/urlHelpers";
import { replaceLinebreaks } from "helpers/commonHelpers";
import CardNew from "components/molecules/Card/CardNew";
import { DateTimeFormat, formatDate } from "helpers/dateTimeHelper";
import { useGetShipmentBasic } from "api/queries/shipments/useGetShipmentBasic";
import { useGetCostRecoverySetting } from "api/queries/location/useGetCostRecoverySetting";

const QuoteOrganisationalDetails = function ({ data, loadingData }) {
  const { getPaymentLocationLabel, permissions } = usePermissions();
  const { state } = useStateMachine();
  const { user_type } = JSON.parse(permissions || "{}");
  const isProvider = user_type === "provider";
  const { data: shipmentData } = useGetShipmentBasic(state.shipmentId, {
    enabled: !!state.shipmentId,
    refetchOnMount: false,
  });
  const { data: costRecoverySetting } = useGetCostRecoverySetting(
    data?.organization?.payment_location_reference,
    { ...(data?.organization?.reason_code && { reason_code: data.organization.reason_code }) },
    {
      enabled: !!data?.organization?.payment_location_reference && !isProvider,
    },
  );

  const {
    shipper,
    title,
    payment_location,
    invoice_location,
    cost_center,
    reason_code,
    reason_code_group,
    custom_field1,
    custom_field2,
    custom_field3,
    custom_longfield_01,
    cost_recovery_required,
    custom_cost_recovery_field1,
    custom_cost_recovery_field2,
    request_creation_datetime,
    requestor,
    requestor_email,
  } = data?.organization || {};

  const { payment_location_reference } = shipmentData?.organization || {};

  const { quick_request_reference, quick_requestor } = data?.quick_request_details?.[0] || {};

  const { is_automated_booking } = shipmentData?.shipment || {};

  const hideOrganisationalDetailsInPrint =
    data?.settings?.hasOwnProperty("hide_organizational_data") && data.settings.hide_organizational_data;

  return (
    <CardNew
      className="mb-15 font-b3"
      isLoading={loadingData}
      title={<FormattedMessage id="quote-request-detail.organisationalDetails" defaultMessage="Organisational details" />}
    >
      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="general.shipper" defaultMessage="Shipper" />
        </Col>
        <Col>{shipper}</Col>
      </Row>

      {quick_requestor?.name && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="initialRequestor" defaultMessage="Initial Requestor" />
          </Col>
          <Col>
            {quick_requestor.name} ({quick_requestor?.email || "-"})
          </Col>
        </Row>
      )}

      {quick_request_reference && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.initialRequestNumber" defaultMessage="Initial Request Number" />
          </Col>
          <Col>
            <a target="_blank" href={`/shipment/${quick_request_reference}/quick`} rel="noreferrer">
              {quick_request_reference}
            </a>
          </Col>
        </Row>
      )}

      {requestor && requestor_email && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="general.createdBy" defaultMessage="Created By" />
          </Col>
          <Col>
            {`${requestor} (${requestor_email})`}{" "}
            {is_automated_booking && (
              <span className="font-italic">
                <FormattedMessage id="quote-request-detail.bookedBySystem" defaultMessage="*booked by system" />
              </span>
            )}
          </Col>
        </Row>
      )}

      <Row className="mb-2">
        <Col className="text-secondary">
          <FormattedMessage id="general.createdDate" defaultMessage="Creation Date" />
        </Col>
        <Col>{formatDate(request_creation_datetime, DateTimeFormat.fullDateTimeFormat)}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">
          {getLabelWithTranslationFallback("Shipment title", {
            style: "capitalize-first",
            paymentLocation: payment_location_reference,
            translation: <FormattedMessage id="billing.shipmentTitle" defaultMessage="Shipment title" />,
          })}
        </Col>
        <Col>{title || "-"}</Col>
      </Row>
      <Row className="mb-2">
        <Col className="text-secondary">{getPaymentLocationLabel()}</Col>
        <Col>{payment_location || "-"}</Col>
      </Row>
      {invoice_location && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="quote-request-detail.invoiceLocation" defaultMessage="Invoice location" />
          </Col>
          <Col>
            <span>{invoice_location || "-"}</span>

            {invoice_location !== payment_location && (
              <span className="pl-2">
                <OverlayTrigger
                  key="right"
                  placement="right"
                  overlay={
                    <Tooltip>
                      <strong>
                        <FormattedMessage
                          id="quote-request-detail.invoiceLocationTooltip"
                          defaultMessage="NOTE: The invoice location will be used while generating the invoice for this shipment."
                        />
                      </strong>
                    </Tooltip>
                  }
                >
                  <FontAwesomeIcon icon={faQuestionCircle} className="font-b1" />
                </OverlayTrigger>
              </span>
            )}
          </Col>
        </Row>
      )}
      {cost_center && (
        <Row className={`mb-2 ${hideOrganisationalDetailsInPrint ? "hide-during-print" : ""}`}>
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Cost Center / WBS / Project #", {
              paymentLocation: payment_location_reference,
              translation: <FormattedMessage id="quote-request-detail.costCenter" defaultMessage="Cost center" />,
            })}
          </Col>
          <Col>{cost_center || "-"}</Col>
        </Row>
      )}
      {reason_code_group && (
        <Row className={`mb-2 ${hideOrganisationalDetailsInPrint ? "hide-during-print" : ""}`}>
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Reason code group", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: <FormattedMessage id="quote-request-detail.reasonCodeGroup" defaultMessage="Reason code group" />,
            })}
          </Col>
          <Col>{reason_code_group || "-"}</Col>
        </Row>
      )}
      {reason_code && (
        <Row className={`mb-2 ${hideOrganisationalDetailsInPrint ? "hide-during-print" : ""}`}>
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Reason code", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: <FormattedMessage id="quote-request-detail.reasonCode" defaultMessage="Reason code" />,
            })}
          </Col>
          <Col>{reason_code || "-"}</Col>
        </Row>
      )}
      {custom_field1 && (
        <Row className={`mb-2 ${hideOrganisationalDetailsInPrint ? "hide-during-print" : ""}`}>
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom field 01", {
              srtyle: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField1"
                  defaultMessage="Custom field 1"
                />
              ),
            })}
          </Col>
          <Col>{custom_field1 || "-"}</Col>
        </Row>
      )}
      {custom_field2 && (
        <Row className={`mb-2 ${hideOrganisationalDetailsInPrint ? "hide-during-print" : ""}`}>
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom field 02", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField2"
                  defaultMessage="Custom field 2"
                />
              ),
            })}
          </Col>
          <Col>{custom_field2 || "-"}</Col>
        </Row>
      )}
      {custom_field3 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom field 03", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customField3"
                  defaultMessage="Custom field 3"
                />
              ),
            })}
          </Col>
          <Col>{custom_field3 || "-"}</Col>
        </Row>
      )}
      {custom_longfield_01 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            {getLabelWithTranslationFallback("Custom longfield 01", {
              style: "capitalize",
              paymentLocation: payment_location_reference,
              translation: (
                <FormattedMessage
                  id="new-request.quote-confirmation.organisation.customLongfield1"
                  defaultMessage="Custom longfield 1"
                />
              ),
            })}
          </Col>
          <Col className="pre-wrap">{ReactHtmlParser(replaceLinebreaks(replaceLinks(custom_longfield_01 || "-")))}</Col>
        </Row>
      )}
      {costRecoverySetting?.is_cost_recovery_required && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage id="tasks.costRecovery" defaultMessage="Cost recovery" />
          </Col>
          <Col>{cost_recovery_required ? "Yes" : "No"}</Col>
        </Row>
      )}
      {custom_cost_recovery_field1 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.organisation.customCostRecoveryField1"
              values={{
                customCostRecoveryField1: getLabel(
                  "Custom cost recovery field 01",
                  "capitalize",
                  payment_location_reference,
                ),
              }}
              defaultMessage="Custom cost recovery field 1"
            />
          </Col>
          <Col>{custom_cost_recovery_field1 || "-"}</Col>
        </Row>
      )}
      {custom_cost_recovery_field2 && (
        <Row className="mb-2">
          <Col className="text-secondary">
            <FormattedMessage
              id="new-request.quote-confirmation.organisation.customCostRecoveryField2"
              values={{
                customCostRecoveryField2: getLabel(
                  "Custom cost recovery field 02",
                  "capitalize",
                  payment_location_reference,
                ),
              }}
              defaultMessage="Custom cost recovery field 2"
            />
          </Col>
          <Col>{custom_cost_recovery_field2 || "-"}</Col>
        </Row>
      )}
    </CardNew>
  );
};

QuoteOrganisationalDetails.propTypes = {
  data: PropTypes.instanceOf(Object).isRequired,
  loadingData: PropTypes.bool.isRequired,
};

export default QuoteOrganisationalDetails;
